<template>
  <div class="form-list store-qr-page">
    <div class="page-title-row">
      <div>
        <h2>门店二维码</h2>
        <p class="info-color">学员扫码购买后，门店信息会自动带入订单，业绩归属当前门店。</p>
      </div>
      <v-select-store
        v-if="isAdministrator"
        v-model="storeId"
        class="store-select"
        placeholder="请选择门店"
      />
      <div v-else class="store-label">{{ store.store_name }}</div>
    </div>

    <el-tabs v-model="tab">
      <el-tab-pane label="门店签到码" name="store">
        <div v-loading="isLoading" class="store-qr-card">
          <div>
            <h3>{{ store.store_name || '请选择门店' }}</h3>
            <p class="info-color">学员扫码进入门店详情，用于签到、查看门店信息。</p>
          </div>
          <el-button type="primary" :disabled="!storeId" :loading="isStoreQrLoading" @click="showStoreQr">查看二维码</el-button>
        </div>
      </el-tab-pane>

      <el-tab-pane label="课次券码" name="card_ticket">
        <div v-loading="isLoading" class="qr-grid">
          <div v-for="item in cardTicketTypes" :key="item.id" class="qr-item">
            <div class="qr-item-title">{{ item.card_ticket_type_name }}</div>
            <div class="qr-item-meta">售价：{{ item.price | toYuan }} 元</div>
            <div class="qr-item-meta">次数：{{ item.expire_number | toYuan }} 次，有效期 {{ item.expire_days | toYuan }} 天</div>
            <el-tag size="small" :type="cardScope(item).tagType">{{ cardScope(item).name }}</el-tag>
            <el-button class="qr-item-button" size="small" type="primary" plain :loading="isQrLoading(item, 1)" @click="showItemQr(item, 1)">
              {{ item.qr_code ? '查看二维码' : '生成二维码' }}
            </el-button>
          </div>
          <div v-if="!isLoading && !cardTicketTypes.length" class="info-color empty-tip">当前门店暂无可售课次券。</div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="代金券码" name="voucher">
        <div v-loading="isLoading" class="qr-grid">
          <div v-for="item in voucherTypes" :key="item.id" class="qr-item">
            <div class="qr-item-title">{{ item.voucher_type_name }}</div>
            <div class="qr-item-meta">面额：{{ item.amount_in_cents | toYuan }} 元，售价：{{ item.sale_price_in_cents | toYuan }} 元</div>
            <el-tag size="small" :type="voucherScope(item).tagType">{{ voucherScope(item).name }}</el-tag>
            <el-button class="qr-item-button" size="small" type="primary" plain :loading="isQrLoading(item, 2)" @click="showItemQr(item, 2)">
              {{ item.qr_code ? '查看二维码' : '生成二维码' }}
            </el-button>
          </div>
          <div v-if="!isLoading && !voucherTypes.length" class="info-color empty-tip">当前门店暂无可售代金券。</div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :visible.sync="qrDialog" width="360px" center>
      <div class="qr-dialog">
        <h3>{{ qrTitle }}</h3>
        <img v-if="qrImage" :src="qrImage" alt="门店二维码"/>
        <p class="info-color">{{ qrTip }}</p>
        <a v-if="qrImage" :href="qrImage" target="_blank" rel="noopener" class="primary-color">保存图片</a>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getQrCodeByStoreId, getStoreItemQrCodes, createStoreItemQrCode } from '@/api/store';
import { findCardTicketPurchaseScope } from '@/enums/cardTicketPurchaseScopes';
import { findVoucherPurchaseScopeItem } from '@/enums/voucherPurchaseScopes';

const CARD_TICKET = 1;

export default {
  data: () => ({
    storeId: '',
    store: {},
    cardTicketTypes: [],
    voucherTypes: [],
    tab: 'store',
    isLoading: false,
    // 门店签到码请求中
    isStoreQrLoading: false,
    // 课次券/代金券二维码请求中，key 为 `${type}-${id}`，保证只锁住被点击的那张卡片
    qrLoadingKeys: {},
    qrDialog: false,
    qrImage: '',
    qrTitle: '',
    qrTip: '',
  }),
  computed: {
    ...mapGetters(['isAdministrator', 'userStoreId']),
  },
  watch: {
    storeId(value) {
      if (value) {
        this.load();
      } else {
        this.store = {};
        this.cardTicketTypes = [];
        this.voucherTypes = [];
      }
    },
  },
  created() {
    if (!this.isAdministrator) {
      this.storeId = this.userStoreId;
    }
  },
  methods: {
    cardScope(item) {
      return findCardTicketPurchaseScope(item);
    },
    voucherScope(item) {
      return findVoucherPurchaseScopeItem(item.purchase_scope);
    },
    async load() {
      if (!this.storeId) return;
      this.isLoading = true;
      const { isError, data } = await getStoreItemQrCodes({ store_id: this.storeId });
      this.isLoading = false;
      if (isError) return;

      this.store = data.store || {};
      this.cardTicketTypes = data.card_ticket_types || [];
      this.voucherTypes = data.voucher_types || [];
    },
    openQr(image, title, tip) {
      this.qrImage = image;
      this.qrTitle = title;
      this.qrTip = tip;
      this.qrDialog = true;
    },
    // 每张券码卡片独立的 loading，避免一张卡片请求中把所有卡片按钮都锁住
    isQrLoading(item, type) {
      return !!this.qrLoadingKeys[`${type}-${item.id}`];
    },
    async showStoreQr() {
      if (!this.storeId || this.isStoreQrLoading) return;

      this.isStoreQrLoading = true;
      const { isError, data } = await getQrCodeByStoreId(this.storeId);
      this.isStoreQrLoading = false;
      if (isError || !data.qr_code) return;
      this.openQr(
        data.qr_code,
        (this.store.store_name || '') + ' · 门店签到码',
        '学员扫码进入门店详情，用于签到、查看门店信息。',
      );
    },
    async showItemQr(item, type) {
      if (!this.storeId) return;
      if (item.qr_code) {
        this.openItemQr(item, type);
        return;
      }

      const loadingKey = `${type}-${item.id}`;
      if (this.qrLoadingKeys[loadingKey]) return;

      this.$set(this.qrLoadingKeys, loadingKey, true);
      const { isError, data } = await createStoreItemQrCode({
        store_id: this.storeId,
        type,
        target_id: item.id,
      });
      this.$set(this.qrLoadingKeys, loadingKey, false);
      if (isError || !data.qr_code) return;
      item.qr_code = data.qr_code;
      this.openItemQr(item, type);
    },
    openItemQr(item, type) {
      const name = type === CARD_TICKET ? item.card_ticket_type_name : item.voucher_type_name;
      this.openQr(
        item.qr_code,
        (this.store.store_name || '') + ' · ' + name,
        type === CARD_TICKET
          ? '学员扫码进入课次券详情，购买后自动归属当前门店。'
          : '学员扫码进入代金券详情，购买后自动归属当前门店。',
      );
    },
  },
};
</script>

<style scoped>
/* 白底由 .form-list 铺满内容区，页面根节点不要再限宽，否则屏幕右侧会露出一整块页面背景色 */
.page-title-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.page-title-row h2 {
  margin: 0 0 8px;
}

.page-title-row p {
  margin: 0;
}

.store-select {
  width: 260px;
}

.store-label {
  font-size: 16px;
  font-weight: 500;
}

.store-qr-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.store-qr-card h3 {
  margin: 0 0 10px;
}

.store-qr-card p {
  margin: 0;
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.qr-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  padding: 18px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.qr-item-title {
  font-size: 16px;
  font-weight: 500;
}

.qr-item-meta {
  color: #606266;
  font-size: 13px;
}

.qr-item-button {
  margin-top: 4px;
}

.empty-tip {
  padding: 24px 0;
}

.qr-dialog {
  text-align: center;
}

.qr-dialog h3 {
  margin: 0 0 16px;
  font-size: 16px;
}

.qr-dialog img {
  width: 240px;
  height: 240px;
}

.qr-dialog p {
  margin: 12px 0;
  font-size: 13px;
  line-height: 20px;
}
</style>
